import { useEffect, useState } from "react"
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Snackbar, TextField, Typography } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'
import { formatDateHourMinute } from "../../utils/strings"
import { qrUrl, reenviarPase } from "../../services/evento"

export interface ParticipanteI {
    id: number
    nombreProfesor: string
    email: string
    edad: string
    ciudad: string
    puesto: string
    nivelEducativo: string
    comoSeEntero: string
    interes: string
    codigoRegistro: string
    fechaAsistencia: string
    fechaAlta: string
}

interface Props {
    participante: ParticipanteI | null
    onClose: () => void
}

interface Feedback {
    type: 'success' | 'error'
    message: string
}

const Field = ({ label, value }: { label: string; value: string }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body1">{value || '—'}</Typography>
    </Grid>
)

const ParticipanteModal = ({ participante, onClose }: Props) => {

    const [qrError, setQrError] = useState(false)
    const [sending, setSending] = useState(false)
    const [feedback, setFeedback] = useState<Feedback | null>(null)
    const [email, setEmail] = useState('')

    const codigo = participante?.codigoRegistro

    // El componente no se desmonta al cambiar de participante, hay que limpiar a mano
    useEffect(() => {
        setQrError(false)
        setSending(false)
        setEmail(participante?.email ?? '')
    }, [codigo, participante?.email])

    if (!participante) return null

    const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

    const handleReenviar = async () => {
        setSending(true)
        try {
            await reenviarPase({
                nombre: participante.nombreProfesor,
                email: email.trim(),
                codigo: participante.codigoRegistro,
            })
            setFeedback({ type: 'success', message: `Pase reenviado a ${email.trim()}` })
        } catch (error) {
            // El fallo de correo no rompe nada más: se loguea y se avisa
            console.error('Error al reenviar el pase:', error)
            const message = error instanceof Error ? error.message : 'Error al enviar el correo'
            setFeedback({ type: 'error', message })
        } finally {
            setSending(false)
        }
    }

    return (
        <>
            <Dialog open={!!participante} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {participante.nombreProfesor}
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Field label="Código de registro" value={participante.codigoRegistro} />
                        <Field label="Email" value={participante.email} />
                        <Field label="Edad" value={participante.edad} />
                        <Field label="Ciudad" value={participante.ciudad} />
                        <Field label="Puesto" value={participante.puesto} />
                        <Field label="Nivel educativo" value={participante.nivelEducativo} />
                        <Field label="¿Cómo se enteró?" value={participante.comoSeEntero} />
                        <Field label="Interés" value={participante.interes} />
                        <Field label="Fecha de asistencia" value={participante.fechaAsistencia ? formatDateHourMinute(participante.fechaAsistencia) : '—'} />
                        <Field label="Fecha de registro" value={participante.fechaAlta ? formatDateHourMinute(participante.fechaAlta) : '—'} />
                    </Grid>

                    <Divider sx={{ my: 2.5 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Pase del evento
                        </Typography>

                        {qrError ? (
                            <Alert severity="warning" sx={{ width: '100%' }}>
                                No se encontró el QR de {participante.codigoRegistro} en el servidor.
                            </Alert>
                        ) : (
                            <img
                                src={qrUrl(participante.codigoRegistro)}
                                alt={`QR ${participante.codigoRegistro}`}
                                width={200}
                                height={200}
                                onError={() => setQrError(true)}
                                style={{ border: '1px solid #e0e0e0', borderRadius: 8 }}
                            />
                        )}

                        <TextField
                            label="Enviar a"
                            type="email"
                            size="small"
                            fullWidth
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            error={!!email && !emailValido}
                            helperText={!!email && !emailValido ? 'Correo no válido' : ' '}
                            sx={{ mt: 1 }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<EmailIcon />}
                            onClick={handleReenviar}
                            disabled={sending || qrError || !emailValido}
                            sx={{
                                background: '#000',
                                textTransform: 'uppercase',
                                fontWeight: 500,
                                px: 4,
                                '&:hover': { background: '#222' },
                            }}
                        >
                            {sending ? 'Enviando...' : 'Reenviar correo'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={!!feedback}
                autoHideDuration={6000}
                onClose={() => setFeedback(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={feedback?.type ?? 'success'} onClose={() => setFeedback(null)}>
                    {feedback?.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default ParticipanteModal
